import Vue from "vue";
import lodash from "lodash";
import router from "../../imports/router";
import externals from "@/constants/externals";

const state = {
  data: [],

  currentJob: false,

  currentTab: 0,

  jobStatuses: [
    "Inquiry",
    "Scheduled",
    "Quoted",
    "Work Order",
    "Invoiced",
    "Completed",
    "Warranty",
    "Dormant",
    "Transferred",
    "Unserviced Area",
    "Unserviced Availability",
  ],

  jobStatusesCreate: [
    "Inquiry",
    "Scheduled",
    "Quoted",
    "Invoiced",
    "Completed",
    "Warranty",
    "Dormant",
    "Transferred",
    "Unserviced Area",
    "Unserviced Availability",
  ],

  advancedSearchFields: [
    "Job Contact Phone/Mobile",
    "Job & Billing Address",
    "Contact First Name",
    "Contact Last Name",
    "Company Name",
  ],

  foundViaOptions: {
    regular: [],
    readonly: [],
  },

  initialised: false,
};

/* eslint-disable no-shadow, no-param-reassign */
const getters = {
  getJobById: (state) => (id) => {
    const index = state.data.map((job) => job.id).indexOf(parseInt(id, 10));
    let record = state.data[index];

    if (!record) {
      record = {
        _new: true,
        user: {},
        customer: {},
        appointments: [],
      };
    }

    return record;
  },
};

const queryJobById = (id) =>
  Vue.http.get(`/jobs/${id}`, {
    params: {
      with: "jobContacts;jobAddress;serviceTypes",
    },
  });

const queryJobs = (payload) =>
  Vue.http.get("/jobs", {
    params: {
      search: lodash.get(payload, "search", ""),
      page: lodash.get(payload, "page", 1),
      sortedBy: lodash.get(payload, "order", "desc"),
      technicianId: lodash.get(payload, "technicianId"),
      brandId: lodash.get(payload, "brandId"),
      serviceTypeId: lodash.get(payload, "serviceTypeId"),
      status: lodash.get(payload, "status"),
      rescheduling: lodash.get(payload, "rescheduling", false),
      withAcceptedQuotes: lodash.get(payload, "withAcceptedQuotes", false),
      recurring: lodash.get(payload, "recurring", false),
      today: lodash.get(payload, "today", false),
      followUp: lodash.get(payload, "followUp"),
      fullSearch: lodash.get(payload, "fullSearch", false),
      customerId: lodash.get(payload, "customerId", false),
      restriction: lodash.get(payload, "restriction", false),
      orderBy: "id",
      with: "brand;jobContacts;jobAddress;serviceTypes",
      limit: 10,
    },
  });

const actions = {
  initialise: ({ state }) => {
    return Vue.http.get("/jobs/found-via?format=types").then((response) => {
      state.foundViaOptions = response.body;

      state.initialised = true;
    });
  },

  // eslint-disable-next-line no-empty-pattern
  create: ({}, payload) =>
    new Promise((resolve, reject) =>
      Vue.http
        .post("/jobs", {
          status: "Draft",
          found_via: "Google",
          customer_id: payload && payload.customer_id ? payload.customer_id : null,
        })
        .then((response) => {
          if (payload && payload.newWindow) {
            window.open(`/jobs/${response.body.id}/edit`);
          } else {
            Vue.router.push({
              name: "jobs.edit",
              params: { id: response.body.id },
            });
          }

          resolve(response);
        })
        .catch((response) => {
          reject(response);
        })
    ),

  loadList: ({ commit }, payload) =>
    new Promise((resolve, reject) => {
      queryJobs(payload)
        .then((response) => {
          commit("loadData", response.body.data);
          resolve(response);
        })
        .catch((response) => {
          reject(response);
        });
    }),

  loadMore: ({ commit }, payload) =>
    new Promise((resolve, reject) => {
      queryJobs(payload)
        .then((response) => {
          commit("appendData", response.body.data);
          resolve(response);
        })
        .catch((response) => {
          reject(response);
        });
    }),

  refreshJob: ({ commit }, id) =>
    new Promise((resolve, reject) => {
      queryJobById(id)
        .then((response) => {
          commit("updateRecord", response.body);
          resolve(response);
        })
        .catch((response) => {
          reject(response);
        });
    }),

  reset: ({ state, dispatch, commit }, payload) =>
    new Promise((resolve, reject) => {
      dispatch("loadList", payload.listQuery || {})
        .then((response) => {
          commit("tech-availability/resetSelectedAppointment", null, { root: true });

          if (payload.navigateAfterReset) {
            if (state.data.length > 0 && lodash.has(state, "data[0].id")) {
              const id = lodash.get(state, "data[0].id");

              if (id !== lodash.get(state, "currentJob.id") || !lodash.has(router, "params.id")) {
                // if (lodash.get(router, 'currentRoute.name') === 'jobs.edit' && lodash.get(router, 'currentRoute.params.id') === id) {
                //   return;
                // }

                router.push({
                  name: "jobs.edit",
                  params: { id },
                });
              }
            } else {
              if (externals && externals.dash && externals.dash.domain) {
                window.location = externals.dash.domain;
              } else {
                router.push({
                  name: "jobs.no-access",
                });
              }
            }
          }

          resolve(response);
        })
        .catch((response) => {
          reject(response);
        });
    }),
};

const mutations = {
  setJobTab(state, payload) {
    state.currentTab = payload;
  },
  loadData(state, payload) {
    state.data = payload;
    state.initialised = true;
  },
  appendData(state, payload) {
    if (state.data.length === 1 && payload.length > 0 && state.data[0].id === payload[0].id) {
      // We have a duplicate record, remove it
      payload.shift();
    }

    state.data = state.data.concat(payload);
  },
  updateRecord(state, payload) {
    // Find the index of the job record in our store.data
    const index = state.data.map((job) => job.id).indexOf(parseInt(payload.id, 10));

    if (index === -1) {
      // Can't find it, assume it's a new record or loaded directly via url
      // @important
      // 2025-05-21 change to load full job data from state.currentJob (with appointments updated from payload)
      // instead of just unshift payload into it (payload is missing other job data)
      state.currentJob.appointments = payload.appointments;
      state.data.unshift(state.currentJob);
    } else {
      // Have to do this otherwise the list doesn't get updated
      const data = window.clone(state.data);
      data[index] = Object.assign(data[index], payload);
      state.data = data;
    }
  },
  removeAppointment(state, payload) {
    const jobId = parseInt(payload.job_id, 10);
    const appointmentId = parseInt(payload.id, 10);

    // Find the index of the job record in our store.data
    const jobIndex = state.data.map((job) => job.id).indexOf(jobId);
    if (state.data[jobIndex].appointments) {
      const { appointments } = state.data[jobIndex];
      const appointmentIndex = appointments
        .map((appointment) => appointment.id)
        .indexOf(appointmentId);

      // Remove it by index
      state.data[jobIndex].appointments.splice(appointmentIndex, 1);
    }
  },
  setCurrentJob(state, payload) {
    state.currentJob = payload;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
